import { useSafeAreaInsets } from 'react-native-safe-area-context';

import {
  ReferencedInquirySummary,
  referencedInquiryLabel,
  referencedInquiryParticipant,
} from './ReferencedInquirySummary';

import {
  Actionsheet,
  ActionsheetBackdrop,
  ActionsheetContent,
  ActionsheetDragIndicator,
  ActionsheetDragIndicatorWrapper,
  ActionsheetItem,
  ActionsheetScrollView,
} from '@/components/ui/actionsheet';
import { Button, ButtonText } from '@/components/ui/button';
import { Heading } from '@/components/ui/heading';
import { HStack } from '@/components/ui/hstack';
import { Spinner } from '@/components/ui/spinner';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { RECENT_INQUIRY_LIMIT, type MemberInquiry } from '@/services/conversations/memberInquiries';
import {
  toReferencedInquiry,
  type ReferencedInquiry,
} from '@/services/conversations/referencedInquiry';
/** How much of the question a chip carries when no participant is named. */
const CHIP_QUESTION_LENGTH = 40;

/**
 * The chip's words for the attached inquiry. The named participant when there
 * is one; otherwise the start of the question, since "No expert assigned yet"
 * alone does not say which inquiry is attached.
 */
export function attachedInquiryChipTitle(details: ReferencedInquiry | null): string {
  if (!details) {
    return 'Linked inquiry';
  }

  if (details.counterpart) {
    return referencedInquiryParticipant(details);
  }

  const question = details.question?.trim();

  if (question) {
    return question.length > CHIP_QUESTION_LENGTH
      ? `${question.slice(0, CHIP_QUESTION_LENGTH).trimEnd()}…`
      : question;
  }

  return referencedInquiryParticipant(details);
}

type AttachInquirySheetProps = {
  isOpen: boolean;
  onClose: () => void;
  memberName: string;
  inquiries: MemberInquiry[] | undefined;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onSelect: (inquiry: MemberInquiry) => void;
};

/**
 * Lists the member's most recent inquiries, newest first, for staff to attach
 * one to their next message, each named the way its card in the message will
 * be: who it is with, the opening question, cancer type, date and whether it
 * is open now. Everything arrives as props: the sheet renders in
 * the root overlay, outside the thread screen's providers, so it cannot read
 * the screen's context or queries itself.
 */
export function AttachInquirySheet({
  isOpen,
  onClose,
  memberName,
  inquiries,
  isLoading,
  isError,
  onRetry,
  onSelect,
}: AttachInquirySheetProps) {
  const { bottom } = useSafeAreaInsets();

  return (
    <Actionsheet isOpen={isOpen} onClose={onClose}>
      <ActionsheetBackdrop />
      <ActionsheetContent className="max-h-[75%] w-full p-0" style={{ paddingBottom: bottom }}>
        <ActionsheetDragIndicatorWrapper>
          <ActionsheetDragIndicator />
        </ActionsheetDragIndicatorWrapper>

        <VStack className="w-full gap-1 px-5 pb-3 pt-2">
          <Heading size="md" className="text-typography-900">
            Attach an inquiry
          </Heading>
          <Text className="text-sm text-typography-700">
            {`${memberName}’s ${RECENT_INQUIRY_LIMIT} most recent`}
          </Text>
        </VStack>

        <ActionsheetScrollView className="w-full" contentContainerClassName="px-2 pb-4">
          <AttachInquiryList
            inquiries={inquiries}
            isLoading={isLoading}
            isError={isError}
            onRetry={onRetry}
            onSelect={onSelect}
          />
        </ActionsheetScrollView>
      </ActionsheetContent>
    </Actionsheet>
  );
}

function AttachInquiryList({
  inquiries,
  isLoading,
  isError,
  onRetry,
  onSelect,
}: Pick<AttachInquirySheetProps, 'inquiries' | 'isLoading' | 'isError' | 'onRetry' | 'onSelect'>) {
  if (isLoading) {
    return (
      <HStack className="items-center justify-center py-10">
        <Spinner accessibilityLabel="Loading inquiries" />
      </HStack>
    );
  }

  if (isError) {
    return (
      <VStack className="items-center gap-3 px-6 py-8">
        <Text className="text-center text-sm text-typography-700" accessibilityLiveRegion="polite">
          Could not load this member’s inquiries.
        </Text>
        <Button variant="outline" size="sm" onPress={onRetry}>
          <ButtonText>Try again</ButtonText>
        </Button>
      </VStack>
    );
  }

  if (!inquiries || inquiries.length === 0) {
    return (
      <Text
        className="px-6 py-8 text-center text-sm text-typography-700"
        accessibilityLiveRegion="polite">
        This member has no inquiries to attach.
      </Text>
    );
  }

  return (
    <VStack>
      {inquiries.map((inquiry) => {
        const reference = toReferencedInquiry(inquiry);

        if (!reference) {
          return null;
        }

        return (
          <ActionsheetItem
            key={inquiry.id}
            onPress={() => onSelect(inquiry)}
            accessibilityRole="button"
            accessibilityLabel={referencedInquiryLabel(reference)}
            className="min-h-[56px] rounded-xl px-3 py-2">
            <ReferencedInquirySummary inquiry={reference} />
          </ActionsheetItem>
        );
      })}
    </VStack>
  );
}
